import { useEffect, useRef, useState } from 'react';
import { GripVertical, Plus, Trash, X } from 'lucide-react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Modal } from '../ui/Modal.js';
import { Field } from '../ui/Field.js';
import { QtyStepper } from '../ui/QtyStepper.js';
import { ConfirmDialog } from '../ui/ConfirmDialog.js';
import { useCatalog } from '../stores/catalog.js';
import { useUi } from '../stores/ui.js';
import { errorMessage } from '../lib/errors.js';

const MAX_URLS = 10;

type UrlRow = { id: string; value: string };

function newUrlRow(value = ''): UrlRow {
  return { id: Math.random().toString(36).slice(2), value };
}

export function CatalogItemModal(): React.ReactElement | null {
  const open = useUi((s) => s.catalogFormOpen);
  const editingId = useUi((s) => s.catalogFormId);
  const close = useUi((s) => s.closeCatalogForm);
  const items = useCatalog((s) => s.items);
  const create = useCatalog((s) => s.create);
  const update = useCatalog((s) => s.update);
  const remove = useCatalog((s) => s.remove);

  const existing = editingId ? items.find((it) => it.itemId === editingId) : null;

  const [name, setName] = useState('');
  const [defaultQty, setDefaultQty] = useState(1);
  const [urls, setUrls] = useState<UrlRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setName(existing.name);
      setDefaultQty(existing.defaultQty);
      setUrls(existing.urls.map((u) => newUrlRow(u)));
    } else {
      setName('');
      setDefaultQty(1);
      setUrls([]);
    }
    setError(null);
    setSaving(false);
  }, [open, existing]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (!open) return null;

  function onDragEnd(e: DragEndEvent): void {
    if (!e.over || e.active.id === e.over.id) return;
    const oldIdx = urls.findIndex((u) => u.id === e.active.id);
    const newIdx = urls.findIndex((u) => u.id === e.over!.id);
    if (oldIdx < 0 || newIdx < 0) return;
    setUrls(arrayMove(urls, oldIdx, newIdx));
  }

  function setUrlValue(id: string, value: string): void {
    setUrls((prev) => prev.map((u) => (u.id === id ? { ...u, value } : u)));
  }

  function addUrl(): void {
    if (urls.length >= MAX_URLS) return;
    setUrls([...urls, newUrlRow()]);
  }

  function removeUrl(id: string): void {
    setUrls((prev) => prev.filter((u) => u.id !== id));
  }

  function validate(cleanUrls: string[]): string | null {
    if (!name.trim()) return 'Le nom est requis';
    if (name.trim().length > 120) return 'Maximum 120 caractères pour le nom';
    for (const u of cleanUrls) {
      if (!u.startsWith('https://')) return `URL invalide : ${u.slice(0, 40)}…`;
    }
    if (new Set(cleanUrls).size !== cleanUrls.length) {
      return 'URLs en double détectées';
    }
    if (cleanUrls.length > MAX_URLS) return `Maximum ${MAX_URLS} URLs`;
    return null;
  }

  async function save(): Promise<void> {
    const cleanUrls = urls.map((u) => u.value.trim()).filter((u) => u.length > 0);
    const err = validate(cleanUrls);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setSaving(true);
    try {
      if (existing) {
        await update(existing.itemId, {
          name: name.trim(),
          urls: cleanUrls,
          defaultQty,
        });
      } else {
        await create({ name: name.trim(), urls: cleanUrls, defaultQty });
      }
      close();
    } catch (e) {
      setError(errorMessage(e, 'Erreur lors de la sauvegarde'));
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!existing) return;
    setConfirmingDelete(false);
    await remove(existing.itemId);
    close();
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={existing ? 'Édition catalogue' : 'Nouvel item'}
      closeLabel="Fermer"
      initialFocusRef={nameRef}
      footer={
        <>
          {existing && (
            <Button variant="danger" className="mr-auto" onClick={() => setConfirmingDelete(true)}>
              <Trash size={16} aria-hidden />
              Supprimer
            </Button>
          )}
          <Button variant="ghost" onClick={close}>
            Annuler
          </Button>
          <Button onClick={save} disabled={!name.trim()} loading={saving}>
            {existing ? 'Enregistrer' : 'Créer'}
          </Button>
        </>
      }
    >
      <div className="pile">
        <Field label="Nom">
          <Input
            ref={nameRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Lessive Ariel, Eau Cristaline 6×1.5L…"
            maxLength={120}
          />
        </Field>

        <Field label="Quantité par défaut" group>
          <QtyStepper value={defaultQty} onChange={setDefaultQty} label="Quantité par défaut" />
        </Field>

        <Field
          label={`URLs Carrefour (${urls.length}/${MAX_URLS})`}
          hint="L'ordre est la priorité : glisse une ligne pour la déplacer."
          group
        >
          {urls.length === 0 ? (
            <div className="vide py-4 border border-dashed border-edge rounded-md">
              <p className="vide-titre">Aucune URL</p>
              <p>
                Colle l'URL d'une page produit Carrefour. Ajoute des URLs supplémentaires en fallback.
              </p>
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={urls.map((u) => u.id)} strategy={verticalListSortingStrategy}>
                <div className="liste liste-aeree">
                  {urls.map((u) => (
                    <UrlRowComponent
                      key={u.id}
                      row={u}
                      onChange={(v) => setUrlValue(u.id, v)}
                      onRemove={() => removeUrl(u.id)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          <Button
            variant="ghost"
            size="small"
            className="justify-self-start"
            onClick={addUrl}
            disabled={urls.length >= MAX_URLS}
          >
            <Plus size={16} aria-hidden />
            Ajouter une URL
          </Button>
        </Field>

        <p className="champ-erreur" role="alert">
          {error}
        </p>
      </div>

      <ConfirmDialog
        open={confirmingDelete}
        title={existing ? `Supprimer "${existing.name}" ?` : 'Supprimer ?'}
        message="Les courses qui référencent cet item seront conservées (avec juste leur titre)."
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        onConfirm={() => void confirmDelete()}
        onCancel={() => setConfirmingDelete(false)}
      />
    </Modal>
  );
}

function UrlRowComponent({
  row,
  onChange,
  onRemove,
}: {
  row: UrlRow;
  onChange: (value: string) => void;
  onRemove: () => void;
}): React.ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: row.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="ligne gap-1 p-0">
      <Button
        {...listeners}
        {...attributes}
        variant="quiet"
        size="icon-small"
        aria-label="Réorganiser cette URL"
        aria-roledescription="sortable"
        className="cursor-grab active:cursor-grabbing"
      >
        <GripVertical size={14} aria-hidden />
      </Button>
      <Input
        value={row.value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://www.carrefour.fr/p/…"
        aria-label="URL du produit"
        className="flex-1 min-w-0"
      />
      <Button variant="quiet" size="icon-small" onClick={onRemove} aria-label="Retirer cette URL">
        <X size={14} aria-hidden />
      </Button>
    </div>
  );
}
